import axios, {
  AxiosError,
  AxiosResponse,
  AxiosRequestConfig,
  AxiosProgressEvent,
} from "axios";

import { CookieKeys } from "@/enums/CookieKeys.enum";
import {
  getCookieUtil,
  setCookieUtil,
  deleteCookieUtil,
} from "@/utils/cookies";

export const api = axios.create({
  baseURL: `${process.env.API_URL}`,
});

function getAccessToken(): string | undefined {
  if (typeof window !== "undefined") {
    return getCookieUtil(CookieKeys.AccessToken);
  }
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires, global-require
    const { cookies } = require("next/headers");
    return cookies().get(CookieKeys.AccessToken)?.value;
  } catch {
    return undefined;
  }
}

export const customClient = async <TResponse>({
  data,
  url,
  method,
  params,
  headers,
  onUploadProgress,
}: {
  data?: unknown;
  url: string;
  signal?: AbortSignal;
  method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
  params?: Record<string, unknown>;
  headers?: { "Content-Type": string };
  onUploadProgress?: (progressEvent: AxiosProgressEvent) => void;
}): Promise<TResponse> => {
  const extraHeaders: Record<string, string> = {};

  if (typeof window === "undefined") {
    extraHeaders["X-MGW-Internal-Key"] = process.env.X_MGW_INTERNAL_KEY || "";
  }

  const accessToken = getAccessToken();
  const authHeaders: Record<string, string> = accessToken
    ? { Authorization: `Bearer ${accessToken}` }
    : {};

  const queryParams = params
    ? `?${new URLSearchParams(
        Object.entries(params)
          .filter(([, v]) => v !== undefined && v !== null)
          .reduce<Record<string, string>>(
            (acc, [k, v]) => ({ ...acc, [k]: String(v) }),
            {}
          )
      )}`
    : "";
  const { data: response } = await api(`${url}${queryParams}`, {
    method,
    data,
    headers: {
      ...authHeaders,
      ...extraHeaders,
      ...headers,
    },
    onUploadProgress:
      method === "POST" || method === "PUT" || method === "PATCH"
        ? onUploadProgress
        : undefined,
  });

  return response;
};

async function handleSuccess(response: AxiosResponse): Promise<AxiosResponse> {
  return response;
}

async function refreshToken(): Promise<string> {
  const response = await axios.post(
    `${process.env.API_URL}/auth/refresh-token`,
    {
      refreshToken: getCookieUtil(CookieKeys.RefreshToken),
    }
  );
  const { accessToken } = response.data;
  setCookieUtil({ key: CookieKeys.AccessToken, value: accessToken });
  return accessToken;
}

async function handleError(error: AxiosError): Promise<unknown> {
  if (error.response?.status === 401 && error.config) {
    try {
      const newAccessToken = await refreshToken();
      const newConfig: AxiosRequestConfig = {
        ...error.config,
        headers: {
          ...error.config.headers,
          Authorization: `Bearer ${newAccessToken}`,
        },
      };
      setCookieUtil({ key: CookieKeys.AccessToken, value: newAccessToken });

      return axios.request(newConfig);
    } catch (refreshError) {
      const axiosError = refreshError as AxiosError;

      if (
        axiosError.response?.status === 401 &&
        window.location.pathname !== "/signin" &&
        !window.location.pathname.includes("verify-account")
      ) {
        deleteCookieUtil(CookieKeys.AccessToken);
        deleteCookieUtil(CookieKeys.RefreshToken);
        const previousUrl = encodeURIComponent(
          window.location.pathname + window.location.search
        );
        window.location.href = `/signin?redirect-to=${previousUrl}`;
        return Promise.reject(refreshError);
      }

      throw refreshError;
    }
  }

  if (error.response) {
    return Promise.reject(error.response.data);
  }

  return Promise.reject(error);
}

api.interceptors.response.use(handleSuccess, handleError);
